/**
 * ActionItemsTimeline Component
 *
 * Displays action items in a chronological timeline with priority color-coding
 */

import { Timeline, Badge, Text, Group, Card, Stack, ActionIcon, Tooltip } from '@mantine/core';
import { IconCircleCheck, IconClock, IconAlertCircle, IconExternalLink } from '@tabler/icons-react';
import type { ActionItem } from '../types/fiConsole';

interface ActionItemsTimelineProps {
  actionItems: ActionItem[];
  onGapClick?: (gapId: string) => void;
  maxItems?: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'blue',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  OPEN: <IconAlertCircle size={16} />,
  IN_PROGRESS: <IconClock size={16} />,
  RESOLVED: <IconCircleCheck size={16} />,
  CLOSED: <IconCircleCheck size={16} />,
};

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'red',
  IN_PROGRESS: 'blue',
  RESOLVED: 'green',
  CLOSED: 'gray',
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'No due date';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`;
  } else if (diffDays === 0) {
    return 'Due today';
  } else if (diffDays <= 7) {
    return `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
};

const isOverdue = (dateString: string | null | undefined, status: string): boolean => {
  if (!dateString || status === 'RESOLVED' || status === 'CLOSED') return false;
  const date = new Date(dateString);
  const now = new Date();
  return date < now;
};

export default function ActionItemsTimeline({
  actionItems,
  onGapClick,
  maxItems = 10,
}: ActionItemsTimelineProps) {
  // Sort by priority (CRITICAL first) and due date
  const sortedItems = [...actionItems]
    .sort((a, b) => {
      // Priority order
      const priorityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
      const priorityDiff =
        priorityOrder[a.priority as keyof typeof priorityOrder] -
        priorityOrder[b.priority as keyof typeof priorityOrder];
      if (priorityDiff !== 0) return priorityDiff;

      // Then by due date
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    })
    .slice(0, maxItems);

  if (sortedItems.length === 0) {
    return (
      <Card withBorder padding="lg">
        <Stack align="center" gap="md" py="xl">
          <IconCircleCheck size={48} color="green" />
          <Text c="dimmed" size="sm">No action items at this time</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Timeline active={sortedItems.length} bulletSize={24} lineWidth={2}>
      {sortedItems.map((item) => {
        const overdue = isOverdue(item.due_date, item.status);
        const timelineColor = overdue ? 'red' : PRIORITY_COLORS[item.priority];

        return (
          <Timeline.Item
            key={item.id}
            bullet={STATUS_ICONS[item.status]}
            title={
              <Group gap="xs" wrap="nowrap">
                <Text fw={600} size="sm" flex={1}>
                  {item.title}
                </Text>
                <Badge color={PRIORITY_COLORS[item.priority]} size="sm">
                  {item.priority}
                </Badge>
                <Badge color={STATUS_COLORS[item.status]} size="sm" variant="light">
                  {item.status.replace('_', ' ')}
                </Badge>
              </Group>
            }
            color={timelineColor}
          >
            <Stack gap="xs" mt="xs">
              {item.description && (
                <Text size="xs" c="dimmed" lineClamp={2}>
                  {item.description}
                </Text>
              )}

              <Group gap="md" wrap="nowrap">
                {item.assigned_to && (
                  <Text size="xs" c="dimmed">
                    <strong>Assigned:</strong> {item.assigned_to}
                  </Text>
                )}

                {item.due_date && (
                  <Text size="xs" c={overdue ? 'red' : 'dimmed'} fw={overdue ? 600 : 400}>
                    <strong>Due:</strong> {formatDate(item.due_date)}
                  </Text>
                )}
              </Group>

              {item.related_guideline_id && (
                <Text size="xs" c="dimmed">
                  <strong>Guideline:</strong> {item.related_guideline_id}
                </Text>
              )}

              {item.related_gap_id && onGapClick && (
                <Group gap="xs">
                  <Tooltip label="View related gap">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={() => onGapClick(item.related_gap_id!)}
                    >
                      <IconExternalLink size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Text size="xs" c="blue">
                    Related Gap: {item.related_gap_id.slice(0, 8)}...
                  </Text>
                </Group>
              )}

              <Text size="xs" c="dimmed">
                Created {new Date(item.created_at).toLocaleDateString()}
              </Text>
            </Stack>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}
